// Buzones de correo: "Mi correo" en Mi perfil (el buzon personal de cada persona) y el formulario para conectar uno.
// La clave del buzon va directo al motor, que prueba IMAP y SMTP antes de guardarla; la Consola no la guarda.
import { conCache } from '../lib/cacheLocal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { buzones, conectarBuzon, type DatosBuzon, empezarSincronizacion, PROVEEDORES, quitarBuzon, type Seguridad, type Servidor } from '../api/correo';
import { useSession } from '../auth/session';
import { ago } from '../lib/format';
import { Icon } from '../lib/icons';
import { readJSON, writeJSON } from '../lib/storage';
import { Pill } from './bits';
import { Dialog } from './Dialog';
import { toast } from './toasts';

export const useBuzones = () =>
  useQuery({
    queryKey: ['buzones'],
    ...conCache('buzones', buzones),
    staleTime: 30_000,
    // Mientras un buzon se sincroniza por primera vez, se refresca seguido.
    refetchInterval: (q) => ((q.state.data ?? []).some((b) => b.canal && b.canal.estado !== 'ACTIVE') ? 15_000 : 120_000),
  });

// Firma del correo: por ahora se guarda en este navegador, una por persona.
const claveFirma = (email = '') => `consola.firma.${email}`;
export const leerFirma = (email = '') => readJSON<string>(claveFirma(email), '');

export function estadoBuzon(b: { fallo: string | null; canal: { estado: string; etapa: string; sincronizado: string | null } | null }): { tono: 'ok' | 'warn' | 'bad'; texto: string } {
  if (b.fallo) return { tono: 'bad', texto: 'El servidor rechazó el acceso' };
  if (!b.canal) return { tono: 'warn', texto: 'Preparando…' };
  if (b.canal.etapa === 'PENDING_CONFIGURATION') return { tono: 'warn', texto: 'Conectado, falta empezar a sincronizar' };
  if (b.canal.estado.startsWith('FAILED')) return { tono: 'bad', texto: 'Falló la sincronización' };
  if (b.canal.estado === 'ACTIVE') return { tono: 'ok', texto: b.canal.sincronizado ? `Al día · ${ago(b.canal.sincronizado)}` : 'Al día' };
  return { tono: 'warn', texto: 'Sincronizando…' };
}

export function MiCorreo() {
  const { session, yo } = useSession();
  const qc = useQueryClient();
  const r = useBuzones();
  const [conectar, setConectar] = useState(false);
  const [quitar, setQuitar] = useState<string | null>(null);
  const [firma, setFirma] = useState(() => leerFirma(yo.email));
  const [activando, setActivando] = useState<string | null>(null);
  const activar = async (id: string) => {
    setActivando(id);
    try {
      await empezarSincronizacion(id);
      void qc.invalidateQueries({ queryKey: ['buzones'] });
      toast('Sincronizando: los correos empiezan a llegar en unos minutos.');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setActivando(null);
    }
  };
  if (session.modo !== 'usuario') return null;
  const mios = (r.data ?? []).filter((b) => !b.compartido);

  const borrar = async (id: string) => {
    if (quitar !== id) return setQuitar(id);
    try {
      await quitarBuzon(id);
      void qc.invalidateQueries({ queryKey: ['buzones'] });
      toast('Buzón desconectado. Los correos ya importados se conservan en las fichas.');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setQuitar(null);
    }
  };

  return (
    <section className="box">
      <h2 className="lbl">Mi correo</h2>
      <p className="note">Tu buzón personal: envías y recibes desde la Bandeja y los correos quedan en la ficha de cada contacto. Solo tú lo usas.</p>
      {r.isLoading ? null : mios.length ? (
        <div className="rows">
          {mios.map((b) => {
            const e = estadoBuzon(b);
            return (
              <div key={b.id} className="row act-row" style={{ padding: '8px 0' }}>
                <span className="ico-dot">
                  <Icon name="mail" />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="nm">{b.correo}</div>
                  <div className="sb">
                    <Pill tone={e.tono} dot>
                      {e.texto}
                    </Pill>
                  </div>
                </div>
                <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {b.canal?.etapa === 'PENDING_CONFIGURATION' ? (
                  <button className="btn sm primary" onClick={() => activar(b.id)} disabled={activando === b.id}>
                    {activando === b.id ? 'Activando…' : 'Empezar a sincronizar'}
                  </button>
                ) : null}
                <button className={`btn sm ${quitar === b.id ? 'danger' : 'ghost'}`} onClick={() => borrar(b.id)}>
                  {quitar === b.id ? 'Sí, desconectar' : 'Desconectar'}
                </button>
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <button className="btn primary" onClick={() => setConectar(true)} style={{ alignSelf: 'flex-start' }}>
          <Icon name="mail" /> Conectar mi correo
        </button>
      )}
      <label className="flabel">
        Firma
        <textarea id="p-firma" rows={3} value={firma} onChange={(e) => setFirma(e.target.value)} onBlur={() => writeJSON(claveFirma(yo.email), firma)} placeholder={`${yo.nombre}\nCargo · Empresa\nTeléfono`} />
      </label>
      {conectar ? <BuzonDialog correoInicial={yo.email} onClose={() => setConectar(false)} /> : null}
    </section>
  );
}

const SEGURIDADES: [Seguridad, string][] = [
  ['SSL_TLS', 'SSL/TLS'],
  ['STARTTLS', 'STARTTLS'],
  ['NONE', 'Ninguna'],
];

function ServidorCampos({ titulo, s, onChange, id }: { titulo: string; s: Servidor; onChange: (s: Servidor) => void; id: string }) {
  return (
    <fieldset className="srv">
      <legend>{titulo}</legend>
      <div className="grid3">
        <label>
          Servidor
          <input id={`${id}-host`} value={s.host} onChange={(e) => onChange({ ...s, host: e.target.value })} placeholder="mail.tuempresa.com" required autoComplete="off" />
        </label>
        <label>
          Puerto
          <input id={`${id}-puerto`} type="number" min={1} max={65535} value={s.puerto} onChange={(e) => onChange({ ...s, puerto: Number(e.target.value) })} required />
        </label>
        <label>
          Seguridad
          <select id={`${id}-seg`} value={s.seguridad} onChange={(e) => onChange({ ...s, seguridad: e.target.value as Seguridad })}>
            {SEGURIDADES.map(([v, t]) => (
              <option key={v} value={v}>
                {t}
              </option>
            ))}
          </select>
        </label>
      </div>
    </fieldset>
  );
}

export function BuzonDialog({ correoInicial = '', onClose }: { correoInicial?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState<DatosBuzon>(() => ({
    correo: correoInicial,
    nombre: '',
    usuario: '',
    clave: '',
    imap: { host: '', puerto: 993, seguridad: 'SSL_TLS' },
    smtp: { host: '', puerto: 465, seguridad: 'SSL_TLS' },
  }));
  const [avanzado, setAvanzado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const dominio = v.correo.split('@')[1] ?? '';
  const conocido = PROVEEDORES.find((p) => p.dominio.test(dominio));
  // Sin servidor escrito: se usa el del proveedor conocido o mail.<dominio>.
  const imap = v.imap.host ? v.imap : (conocido?.imap ?? { ...v.imap, host: dominio ? `mail.${dominio}` : '' });
  const smtp = v.smtp.host ? v.smtp : (conocido?.smtp ?? { ...v.smtp, host: dominio ? `mail.${dominio}` : '' });

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setEnviando(true);
    try {
      await conectarBuzon({ ...v, usuario: v.usuario || v.correo, imap, smtp });
      void qc.invalidateQueries({ queryKey: ['buzones'] });
      toast('Buzón conectado. Los correos empiezan a llegar en unos minutos.');
      onClose();
    } catch (err) {
      setError((err as Error).message);
      setAvanzado(true);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog titulo="Conectar correo" subtitulo="IMAP para recibir y SMTP para enviar, con los datos de tu servidor de correo" onClose={onClose} width={620}>
      <form className="form" onSubmit={enviar}>
        <div className="grid2">
          <label>
            Correo
            <input id="b-correo" type="email" value={v.correo} onChange={(e) => setV({ ...v, correo: e.target.value })} required autoComplete="email" />
          </label>
          <label>
            Contraseña del correo
            <input id="b-clave" type="password" value={v.clave} onChange={(e) => setV({ ...v, clave: e.target.value })} required autoComplete="new-password" />
          </label>
        </div>
        {conocido && !avanzado ? (
          <p className="note">
            <Icon name="check" size={15} /> Servidor reconocido: {conocido.nombre}.
          </p>
        ) : null}
        <button type="button" className="linkish" style={{ alignSelf: 'flex-start' }} onClick={() => setAvanzado((x) => !x)}>
          {avanzado ? 'Ocultar servidores' : 'Ver o cambiar servidores y usuario'}
        </button>
        {avanzado ? (
          <>
            <label>
              Usuario (si es distinto del correo)
              <input id="b-usuario" value={v.usuario} onChange={(e) => setV({ ...v, usuario: e.target.value })} placeholder={v.correo} autoComplete="username" />
            </label>
            <ServidorCampos id="b-imap" titulo="Recibir (IMAP)" s={imap} onChange={(s) => setV({ ...v, imap: s })} />
            <ServidorCampos id="b-smtp" titulo="Enviar (SMTP)" s={smtp} onChange={(s) => setV({ ...v, smtp: s })} />
          </>
        ) : null}
        {error ? <p className="error">{error}</p> : null}
        <p className="note">
          <Icon name="lock" size={15} /> Antes de guardar se prueba la conexión. La contraseña queda cifrada en el servidor del CRM.
        </p>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn primary" disabled={enviando || !v.correo || !v.clave}>
            {enviando ? 'Probando conexión…' : 'Conectar'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
